import { DatePipe } from '@angular/common';
import { Component, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import * as _moment from 'moment';
import { default as _rollupMoment } from 'moment';
import { forkJoin, Observable, of, Subject } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, finalize, timeout } from 'rxjs/operators';
import Swal from 'sweetalert2';

const moment = _rollupMoment || _moment;

// services
import { InvoiceService } from '../../services/invoice.service';
import { ReportService } from '../../services/report.service';
import { SettingsService } from '../../services/settings.service';
import { SharedService } from '../../services/shared.service';
// interfaces
import { IInvoiceNewVo } from '../../interfaces/Invoice/iinvoice-new-vo';
import { ISageBalanceForwardDto } from '../../interfaces/Sage/isage-balance-forward-dto';
import { ISageCustomerDto } from '../../interfaces/Sage/isage-customer-dto';
import { ISageCustomerSearchRequestDto } from '../../interfaces/Sage/isage-customer-search-request-dto';
import { IResultObjVo } from '../../interfaces/iresult-obj-vo';
import { IUserActionLogRowVo } from '../../interfaces/Invoice/iuser-action-log-row-vo';
// shared data
import { INVOICE_REVIEW_STATUS } from '../../shared-data/invoice-review-status';
import { INVOICE_SAGE_STATUS } from '../../shared-data/invoice-sage-status';

@Component({
  selector: 'app-invoice-profile-details',
  templateUrl: './invoice-profile-details.component.html',
  styleUrls: ['./invoice-profile-details.component.css']
})
export class InvoiceProfileDetailsComponent implements OnInit, OnDestroy {

  invoiceList: IInvoiceNewVo[] = [];
  sageBalanceForward: ISageBalanceForwardDto = {
    customer: {
      id: 0,
      pharmacyId: 0,
      facilityId: 0,
      patientId: 0,
      sageName: '',
      name: '',
      email: '',
      accountNumber: '',
      contact: '',
      street1: '',
      street2: '',
      city: '',
      province: '',
      country: '',
      postalCode: '',
      paymentType: '',
      status: false,
      canSendEmail: false,
      sageCustomerId: null
    },
    transactionList: [],
    balanceForward: 0
  };

  pharmacyId: number;
  facilityId: number;
  patientId: number;

  showLoadingMessage: boolean;
  showErrorMessage: boolean = false;
  errorMessage: string = '';

  displaySuccessMessage: boolean = false;
  successMessage: string = '';

  reviewStatus = INVOICE_REVIEW_STATUS;
  sageStatus = INVOICE_SAGE_STATUS;

  // Sorting
  sortedData: any[] = [];
  currentSortField: string = '';
  sortDirection: 'asc' | 'desc' = 'desc';

  // Balance forward: year (Fill Month), transaction type, detail type (null = All)
  balanceForwardSelectedYear: number | null = null;
  balanceForwardSelectedTransactionType: string | null = null;
  balanceForwardSelectedDetailType: string | null = null;

  // Profile data for modal editing
  profileData = {
    sageName: '',
    sageCustomerId: null as number | null,
    contact: '',
    email: '',
    accountNumber: '',
    paymentType: '',
    street1: '',
    street2: '',
    city: '',
    province: '',
    postalCode: '',
    country: 'Canada',
    canSendEmail: true
  };

  // Validation flags
  emailInvalid = false;
  postalCodeInvalid = false;

  // Sage Customer search
  sageSearchTerm = '';
  sageSearchResults: ISageCustomerDto[] = [];
  sageSearching = false;
  private sageSearchSubject = new Subject<string>();
  private sageSearchSubscription: any;
  selectedTaxYear: number | null = null;

  annualInvoiceItems: any[] = [];
  annualInvoiceTotalPays: number = 0;
  annualInvoiceTotalHst: number = 0;
  annualInvoiceTotalInvoice: number = 0;
  annualInvoiceLoading: boolean = false;
  annualInvoiceDateBreak: boolean[] = [];
  annualInvoiceDateEnd: boolean[] = [];
  annualInvoiceMonthlySummary: any[] = [];

  showAnnualScrollTopButton: boolean = false;
  showAnnualScrollBottomButton: boolean = false;

  showAnnualTaxReceiptActionLogPanel = true;
  annualTaxReceiptActionLogs: IUserActionLogRowVo[] = [];
  annualTaxReceiptActionLogLoading = false;
  annualTaxReceiptActionLogError = '';

  get taxReceiptPatientNameLastFirst(): string {
    const fullName =
      this.invoiceList && this.invoiceList.length > 0 && this.invoiceList[0] && this.invoiceList[0].billedTo
        ? this.invoiceList[0].billedTo
        : '';

    return fullName;
  }

  get taxReceiptFacilityName(): string {
    return this.invoiceList && this.invoiceList.length > 0 && this.invoiceList[0] && this.invoiceList[0].facilityName
      ? this.invoiceList[0].facilityName
      : '';
  }

  get taxReceiptStreetLine(): string {
    const c = this.sageBalanceForward && this.sageBalanceForward.customer ? this.sageBalanceForward.customer : null;
    if (!c) return '';

    const street1 = c.street1 ? String(c.street1).trim() : '';
    const street2 = c.street2 ? String(c.street2).trim() : '';

    if (street2 && street1) return `${street2} ${street1}`;
    return street2 || street1 || '';
  }

  get taxReceiptCityProvinceLine(): string {
    const c = this.sageBalanceForward && this.sageBalanceForward.customer ? this.sageBalanceForward.customer : null;
    if (!c) return '';

    const city = c.city ? String(c.city).trim() : '';
    const province = c.province ? String(c.province).trim() : '';

    if (city && province) return `${city}, ${province}`;
    return city || province || '';
  }

  get taxReceiptPostalCode(): string {
    const c = this.sageBalanceForward && this.sageBalanceForward.customer ? this.sageBalanceForward.customer : null;
    return this.formatCanadianPostalCode(c && c.postalCode ? String(c.postalCode) : '');
  }

  private balanceForwardRawTransactionList(): any[] {
    return (this.sageBalanceForward && this.sageBalanceForward.transactionList)
      ? this.sageBalanceForward.transactionList
      : [];
  }

  /** After year filter only (Fill Month year) */
  get balanceForwardTransactionsAfterYear(): any[] {
    const list = this.balanceForwardRawTransactionList();
    if (this.balanceForwardSelectedYear == null) {
      return list;
    }
    const year = this.balanceForwardSelectedYear;
    return list.filter(t => this.getFillDateRangeYear(t.transactionDate) === year);
  }

  /** Distinct transactionTypeName in current year scope (Invoice, Deposit, Credit note, etc.) */
  get balanceForwardAvailableTransactionTypes(): string[] {
    const set = new Set<string>();
    this.balanceForwardTransactionsAfterYear.forEach(t => {
      const n = (t.transactionTypeName != null ? String(t.transactionTypeName) : '').trim();
      if (n) {
        set.add(n);
      }
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  }

  /** After year + transaction type */
  get balanceForwardTransactionsAfterYearAndTxType(): any[] {
    let out = this.balanceForwardTransactionsAfterYear;
    if (this.balanceForwardSelectedTransactionType) {
      const sel = this.balanceForwardSelectedTransactionType;
      out = out.filter(t => (t.transactionTypeName != null ? String(t.transactionTypeName) : '').trim() === sel);
    }
    return out;
  }

  /** Distinct transactDetailsTypeName for rows matching year + transaction type */
  get balanceForwardAvailableDetailTypes(): string[] {
    const set = new Set<string>();
    this.balanceForwardTransactionsAfterYearAndTxType.forEach(t => {
      const n = (t.transactDetailsTypeName != null ? String(t.transactDetailsTypeName) : '').trim();
      if (n) {
        set.add(n);
      }
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  }

  /** Filtered table rows */
  get balanceForwardFilteredTransactionList(): any[] {
    let out = this.balanceForwardTransactionsAfterYearAndTxType;
    if (this.balanceForwardSelectedDetailType) {
      const sel = this.balanceForwardSelectedDetailType;
      out = out.filter(t => (t.transactDetailsTypeName != null ? String(t.transactDetailsTypeName) : '').trim() === sel);
    }
    return out;
  }

  pruneBalanceForwardFilters(): void {
    const tx = this.balanceForwardAvailableTransactionTypes;
    if (this.balanceForwardSelectedTransactionType && tx.indexOf(this.balanceForwardSelectedTransactionType) < 0) {
      this.balanceForwardSelectedTransactionType = null;
    }
    const dt = this.balanceForwardAvailableDetailTypes;
    if (this.balanceForwardSelectedDetailType && dt.indexOf(this.balanceForwardSelectedDetailType) < 0) {
      this.balanceForwardSelectedDetailType = null;
    }
  }

  onBalanceForwardYearChange(year: number | null): void {
    this.balanceForwardSelectedYear = year;
    this.pruneBalanceForwardFilters();
  }

  onBalanceForwardTransactionTypeChange(value: string | null): void {
    this.balanceForwardSelectedTransactionType = value;
    const dt = this.balanceForwardAvailableDetailTypes;
    if (this.balanceForwardSelectedDetailType && dt.indexOf(this.balanceForwardSelectedDetailType) < 0) {
      this.balanceForwardSelectedDetailType = null;
    }
  }

  /** Years (by Fill Month) that have at least one transaction; else [currentYear .. 2020] */
  get balanceForwardAvailableYears(): number[] {
    const list = (this.sageBalanceForward && this.sageBalanceForward.transactionList) ? this.sageBalanceForward.transactionList : [];
    const currentYear = new Date().getFullYear();
    if (list.length === 0) {
      const years: number[] = [];
      for (let y = currentYear; y >= 2020; y--) years.push(y);
      return years;
    }
    const set = new Set<number>();
    list.forEach(t => {
      const y = this.getFillDateRangeYear(t.transactionDate);
      if (!isNaN(y)) set.add(y);
    });
    return Array.from(set).sort((a, b) => b - a);
  }

  get balanceInvoiceTotal(): number {
    const list = this.balanceForwardFilteredTransactionList;
    return list.filter(t => t.amount != null && t.amount > 0).reduce((sum, t) => sum + Number(t.amount), 0);
  }

  get balancePaymentTotal(): number {
    const list = this.balanceForwardFilteredTransactionList;
    return list.filter(t => t.amount != null && t.amount < 0).reduce((sum, t) => sum + Number(t.amount), 0);
  }

  /** Total row: overall balance when All year and All type, else sum of filtered amounts */
  get balanceForwardDisplayTotal(): number {
    if (this.balanceForwardSelectedYear == null
        && !this.balanceForwardSelectedTransactionType
        && !this.balanceForwardSelectedDetailType
        && this.sageBalanceForward && this.sageBalanceForward.balanceForward != null) {
      return this.sageBalanceForward.balanceForward;
    }
    return this.balanceInvoiceTotal + this.balancePaymentTotal;
  }

  /** Year of (transaction date minus one month) for Fill Month–based filtering */
  getFillDateRangeYear(transactionDate: string | null): number {
    if (!transactionDate) return NaN;
    const d = new Date(transactionDate);
    if (isNaN(d.getTime())) return NaN;
    d.setMonth(d.getMonth() - 1);
    return d.getFullYear();
  }

  /** When detail type is Unknown, show "TransactionType (Unknown)" e.g. Deposit (Unknown) */
  getBalanceForwardDetailTypeDisplay(transaction: any): string {
    const detail = (transaction.transactDetailsTypeName != null ? String(transaction.transactDetailsTypeName) : '').trim();
    if (detail.toLowerCase() === 'unknown') {
      const tx = (transaction.transactionTypeName != null ? String(transaction.transactionTypeName) : '').trim();
      return tx ? `${tx} (Unknown)` : 'Unknown';
    }
    return detail;
  }

  /** Transaction date minus one month as yyyy-MM for Fill Month column */
  getFillDateRangeMonth(transactionDate: string | null): string {
    if (!transactionDate) return '';
    const d = new Date(transactionDate);
    if (isNaN(d.getTime())) return '';
    d.setMonth(d.getMonth() - 1);
    const y = d.getFullYear();
    const m = d.getMonth() + 1;
    return y + '-' + (m < 10 ? '0' : '') + m;
  }

  private formatCanadianPostalCode(postalCode: string): string {
    if (!postalCode) return '';
    const raw = String(postalCode).toUpperCase().replace(/\s+/g, '').trim();
    if (raw.length === 6) {
      return `${raw.substring(0, 3)} ${raw.substring(3)}`;
    }
    return raw;
  }

  private buildAnnualTaxReceiptFileBaseName(year: number): string {
    const fullName = this.taxReceiptPatientNameLastFirst || '';
    const parsed = this.parseLastFirstNameForFileName(fullName);
    return `Annual-Tax-Receipt-${year}-${parsed.lastName}-${parsed.firstName}`;
  }

  private parseLastFirstNameForFileName(fullName: string): { lastName: string; firstName: string } {
    const cleaned = String(fullName || '').trim().replace(/\s+/g, ' ');

    let last = '';
    let first = '';

    if (cleaned.indexOf(',') >= 0) {
      const parts = cleaned.split(',');
      last = parts[0] ? parts[0].trim() : '';
      first = parts[1] ? parts[1].trim() : '';
    } else {
      const parts = cleaned.split(' ').filter(x => x);
      if (parts.length >= 2) {
        last = parts[parts.length - 1];
        first = parts.slice(0, parts.length - 1).join(' ');
      } else if (parts.length === 1) {
        last = parts[0];
      }
    }

    const lastName = this.sanitizeFileNameSegment(last) || 'LastName';
    const firstName = this.sanitizeFileNameSegment(first) || 'FirstName';

    return { lastName, firstName };
  }

  private sanitizeFileNameSegment(value: string): string {
    return String(value || '').replace(/[^a-zA-Z0-9]+/g, '').trim();
  }

  private getInvoiceRecipientEmail(): string {
    const c = this.sageBalanceForward && this.sageBalanceForward.customer ? this.sageBalanceForward.customer : null;
    const email = c && c.email ? String(c.email).trim() : '';
    return email || '';
  }

  private async getLoggedInUserEmail(): Promise<string> {
    const userId = this.sharedService ? this.sharedService.getUserId() : null;
    if (!userId) return '';

    return new Promise<string>(resolve => {
      try {
        this.settingsService.getUserProfile(userId).subscribe(data => {
          const email = data && data.email ? String(data.email).trim() : '';
          resolve(email);
        }, _err => resolve(''));
      } catch (_e) {
        resolve('');
      }
    });
  }

  private isValidEmail(email: string): boolean {
    if (!email) return false;
    const e = String(email).trim();
    // Simple email validation (good enough for UI selection)
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);
  }

  exportAnnualTaxReceiptPdf() {
    if (this.annualInvoiceLoading) return;

    if (!this.annualInvoiceMonthlySummary || this.annualInvoiceMonthlySummary.length === 0) {
      Swal.fire({
        icon: 'warning',
        title: 'No data to export',
        text: 'Please select a year with invoice data before downloading the Annual Tax Receipt.',
        confirmButtonText: 'OK'
      });
      return;
    }

    Swal.fire({
      icon: 'question',
      title: 'Download Annual Tax Receipt PDF?',
      text: 'Do you want to generate and download the Annual Tax Receipt as a PDF file?',
      showCancelButton: true,
      cancelButtonText: 'Cancel',
      confirmButtonText: 'Yes, download'
    }).then(result => {
      if (!result.isConfirmed) return;

      this.downloadAnnualTaxReceiptPdf();
    });
  }

  private downloadAnnualTaxReceiptPdf() {
    const year = this.selectedTaxYear;
    if (year == null) return;

    const contentEl = document.getElementById('annual-tax-receipt-content');
    const html = contentEl ? String(contentEl.innerHTML) : '';

    // Preferred path: backend generates PDF (Telerik). If not available yet, fall back to Print-to-PDF.
    this.showLoadingMessage = true;
    this.reportService.getAnnualTaxReceiptPdf({
      pharmacyId: this.pharmacyId,
      facilityId: this.facilityId,
      patientId: this.patientId,
      year,
      html,
      performedByUserId: this.sharedService.getUserId()
    })
      .pipe(timeout(6000000))
      .subscribe(blob => {
        if (!blob) {
          this.showLoadingMessage = false;
          this.printAnnualTaxReceiptToPdf(year);
          return;
        }

        const fileUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = fileUrl;

        const now = new Date();
        const formattedDate = now.getFullYear() +
          String(now.getMonth() + 1).padStart(2, '0') +
          String(now.getDate()).padStart(2, '0') +
          String(now.getHours()).padStart(2, '0') +
          String(now.getMinutes()).padStart(2, '0') +
          String(now.getMilliseconds()).padStart(2, '0');

        // Note: For server-generated PDFs we can set the filename directly.
        a.download = `${this.buildAnnualTaxReceiptFileBaseName(year)}.pdf`;
        a.click();
        window.URL.revokeObjectURL(fileUrl);
        this.showLoadingMessage = false;
      }, _error => {
        this.showLoadingMessage = false;
        this.printAnnualTaxReceiptToPdf(year);
      });
  }

  private printAnnualTaxReceiptToPdf(year: number) {
    const contentEl = document.getElementById('annual-tax-receipt-content');
    if (!contentEl) {
      Swal.fire({
        icon: 'error',
        title: 'Unable to export',
        text: 'Annual Tax Receipt content was not found on the page.',
        confirmButtonText: 'OK'
      });
      return;
    }

    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      Swal.fire({
        icon: 'warning',
        title: 'Popup blocked',
        text: 'Please allow popups to export the Annual Tax Receipt.',
        confirmButtonText: 'OK'
      });
      return;
    }

    const title = this.buildAnnualTaxReceiptFileBaseName(year);
    // Build print-only HTML (no UI changes): remove monthly table and show only invoice total.
    const cloned = contentEl.cloneNode(true) as HTMLElement;

    const monthlyTable = cloned.querySelector('.annual-invoice-monthly-table');
    if (monthlyTable) {
      const wrapper = monthlyTable.closest('.table-responsive');
      if (wrapper && wrapper.parentNode) {
        wrapper.parentNode.removeChild(wrapper);
      } else if (monthlyTable.parentNode) {
        monthlyTable.parentNode.removeChild(monthlyTable);
      }
    }

    const total = typeof this.annualInvoiceTotalInvoice === 'number' && !isNaN(this.annualInvoiceTotalInvoice)
      ? this.annualInvoiceTotalInvoice
      : 0;

    const totalLine = document.createElement('div');
    totalLine.className = 'annual-total-paid-line';
    totalLine.innerHTML = `
      <span class="label">Total Amount Paid by the Patient</span>
      <span class="amount-wrap">
        <span class="amount">$ ${total.toFixed(2)}</span>
      </span>
    `;
    cloned.appendChild(totalLine);

    // Keep wrapper id for scoped print CSS rules
    const contentHtml = cloned.outerHTML;

    printWindow.document.open();
    printWindow.document.write(`
      <!doctype html>
      <html>
        <head>
          <meta charset="utf-8" />
          <meta name="viewport" content="width=device-width, initial-scale=1" />
          <title>${title}</title>
          <style>
            body { font-family: Arial, Helvetica, sans-serif; color: #212529; margin: 24px; }
            .text-center { text-align: center; }
            .text-end { text-align: right; }
            .text-start { text-align: left; }
            .fw-bold { font-weight: 700; }
            /* PDF sizing adjustments */
            #annual-tax-receipt-content { font-size: 0.78rem; }
            #annual-tax-receipt-content .fs-4 { font-size: 1.0rem; } /* title: 2 steps smaller */
            /* Personal info lines (pharmacy/phone) should match patient info font-size */
            #annual-tax-receipt-content .fs-5 { font-size: inherit; }
            .text-decoration-underline { text-decoration: underline; }
            /* Add extra blank lines under the title (PDF only) */
            #annual-tax-receipt-content .text-center.fw-bold.fs-4 { margin-bottom: 26px; }
            table { width: 100%; border-collapse: collapse; margin-top: 16px; }
            #annual-tax-receipt-content table th,
            #annual-tax-receipt-content table td { font-size: 0.70rem; padding: 5px; }

            /* Repeat the detail table header on each printed page */
            #annual-tax-receipt-content .annual-invoice-detail-table thead { display: table-header-group; }

            /* Detail table alignment: left for first 4 columns */
            #annual-tax-receipt-content .annual-invoice-detail-table th:nth-child(-n+4),
            #annual-tax-receipt-content .annual-invoice-detail-table td:nth-child(-n+4) {
              text-align: left !important;
            }

            /* Preserve right alignment for currency column */
            #annual-tax-receipt-content .annual-invoice-detail-table th.text-end,
            #annual-tax-receipt-content .annual-invoice-detail-table td.text-end {
              text-align: right !important;
            }

            /* Detail table: show only header bottom + table bottom border */
            #annual-tax-receipt-content .annual-invoice-detail-table th,
            #annual-tax-receipt-content .annual-invoice-detail-table td {
              border: none !important;
            }

            #annual-tax-receipt-content .annual-invoice-detail-table thead th {
              border-bottom: 1px solid #212529 !important;
              background: #ffffff;
            }

            /* Ensure underline prints on repeated headers (page 2+) */
            #annual-tax-receipt-content .annual-invoice-detail-table thead,
            #annual-tax-receipt-content .annual-invoice-detail-table thead tr {
              border-bottom: 1px solid #212529 !important;
            }

            #annual-tax-receipt-content .annual-invoice-detail-table tr.annual-invoice-last-row td {
              border-bottom: 1px solid #212529 !important;
            }

            #annual-tax-receipt-content .annual-total-paid-line {
              margin-top: 18px;
              font-size: 0.70rem;
              display: flex;
              justify-content: flex-end;
              align-items: flex-end;
              gap: 24px;
            }

            #annual-tax-receipt-content .annual-total-paid-line .label {
              font-weight: 700;
              display: inline-block;
              border-bottom: 1px solid #212529;
              padding-bottom: 2px;
              line-height: 1.1;
            }

            #annual-tax-receipt-content .annual-total-paid-line .amount-wrap {
              display: inline-flex;
              flex-direction: column;
              align-items: flex-end;
              text-align: right;
              min-width: 160px;
            }

            #annual-tax-receipt-content .annual-total-paid-line .amount {
              font-weight: 700;
              line-height: 1.1;
              white-space: nowrap;
              display: inline-block;
              border-bottom: 1px solid #212529;
              padding-bottom: 2px;
            }

            #annual-tax-receipt-content .annual-total-paid-line .blank {
              display: inline-block;
              border-bottom: 1px solid #212529;
              padding-bottom: 2px;
              line-height: 1.1;
              white-space: nowrap;
            }

            @media print { a[href]:after { content: ""; } }
          </style>
        </head>
        <body>
          ${contentHtml}
        </body>
      </html>
    `);
    printWindow.document.close();

    // Give the browser a moment to render before printing.
    setTimeout(() => {
      try {
        printWindow.focus();
        printWindow.print();
      } catch (_e) { }
    }, 300);

    Swal.fire({
      icon: 'info',
      title: 'Print to PDF',
      text: 'If the server PDF export is not implemented yet, you can save this report using the Print dialog (Save as PDF).',
      confirmButtonText: 'OK'
    });
  }

  async sendAnnualTaxReceiptEmail() {
    if (this.annualInvoiceLoading) return;

    if (!this.annualInvoiceMonthlySummary || this.annualInvoiceMonthlySummary.length === 0) {
      Swal.fire({
        icon: 'warning',
        title: 'No data to send',
        text: 'Please select a year with invoice data before sending the Annual Tax Receipt.',
        confirmButtonText: 'OK'
      });
      return;
    }

    const invoiceEmail = this.getInvoiceRecipientEmail();
    const userEmail = await this.getLoggedInUserEmail();

    const invoiceRow = invoiceEmail ? invoiceEmail : '<span class="text-muted">(No email found)</span>';
    const userRow = userEmail ? userEmail : '<span class="text-muted">(No email found)</span>';

    const year = this.selectedTaxYear;
    if (year == null) return;

    const fileBaseName = this.buildAnnualTaxReceiptFileBaseName(year);
    const subjectDefault = `Annual Tax Receipt – ${year} | London Medical Pharmacy`;
    const bodyDefault =
      `Good day,\n\n` +
      `Please find attached the annual tax receipt for the ${year} tax year.\n\n` +
      `Resident: ${this.taxReceiptPatientNameLastFirst}\n` +
      (this.taxReceiptFacilityName ? `Facility: ${this.taxReceiptFacilityName}\n` : ``) +
      `Total amount paid (CAD): $${(this.annualInvoiceTotalInvoice || 0).toFixed(2)}\n\n` +
      `Please retain this receipt for your records. It may be used to support eligible ` +
      `medical expense claims on a Canadian income tax return, where applicable.\n\n` +
      `Should you have any questions, please contact our Finance Department toll-free ` +
      `anywhere in Canada:\n` +
      `1-844-519-4949, ext. 4\n\n` +
      `Sincerely,\n\n` +
      `Finance Department\n` +
      `London Medical Pharmacy\n` +
      `London, ON\n` +
      `Canada`
      ;

    Swal.fire({
      icon: 'question',
      title: 'Send Annual Tax Receipt by E-MAIL?',
      width: 800,
      html: `
        <div style="text-align:left;">
          <div class="mb-2 fw-bold">Select recipients</div>

          <div class="form-check mb-2">
            <input class="form-check-input" type="checkbox" id="tax-email-invoice" ${invoiceEmail ? '' : 'disabled'}>
            <label class="form-check-label" for="tax-email-invoice">Invoice recipient email</label>
            <div class="text-muted swal-recipient-email" style="margin-left: 24px;">${invoiceRow}</div>
          </div>

          <div class="form-check mb-2">
            <input class="form-check-input" type="checkbox" id="tax-email-user" ${userEmail ? '' : 'disabled'}>
            <label class="form-check-label" for="tax-email-user">Current logged-in user email</label>
            <div class="text-muted swal-recipient-email" style="margin-left: 24px;">${userRow}</div>
          </div>

          <div class="form-check mb-2">
            <input class="form-check-input" type="checkbox" id="tax-email-custom-check">
            <label class="form-check-label" for="tax-email-custom-check">Custom email</label>
          </div>
          <input id="tax-email-custom-input" class="swal2-input" placeholder="Enter email address" style="margin: 6px 0 0 0; width: 100%;" />

          <hr class="my-3" />
          <div class="mb-2 fw-bold">Email content</div>
          <input id="tax-email-subject" class="swal2-input" placeholder="Subject" style="margin: 6px 0 0 0; width: 100%;" value="${subjectDefault.replace(/"/g, '&quot;')}" />
          <textarea id="tax-email-body" class="swal2-textarea" placeholder="Email body" rows="10" style="margin: 6px 0 0 0; width: 100%; height: auto;">${bodyDefault}</textarea>
        </div>
      `,
      showCancelButton: true,
      cancelButtonText: 'Cancel',
      confirmButtonText: 'Yes, send',
      preConfirm: () => {
        const inv = document.getElementById('tax-email-invoice') as any;
        const usr = document.getElementById('tax-email-user') as any;
        const customCheck = document.getElementById('tax-email-custom-check') as any;
        const customInput = document.getElementById('tax-email-custom-input') as any;
        const subjectEl = document.getElementById('tax-email-subject') as any;
        const bodyEl = document.getElementById('tax-email-body') as any;

        const emails: string[] = [];

        if (inv && inv.checked) {
          if (!invoiceEmail || !this.isValidEmail(invoiceEmail)) {
            Swal.showValidationMessage('Invoice recipient email is missing or invalid.');
            return null;
          }
          emails.push(invoiceEmail);
        }

        if (usr && usr.checked) {
          if (!userEmail || !this.isValidEmail(userEmail)) {
            Swal.showValidationMessage('Current user email is missing or invalid.');
            return null;
          }
          emails.push(userEmail);
        }

        if (customCheck && customCheck.checked) {
          const customVal = customInput && customInput.value ? String(customInput.value).trim() : '';
          if (!customVal) {
            Swal.showValidationMessage('Please enter a custom email address.');
            return null;
          }
          if (!this.isValidEmail(customVal)) {
            Swal.showValidationMessage('Custom email address is invalid.');
            return null;
          }
          emails.push(customVal);
        }

        const unique = Array.from(new Set(emails.filter(x => x)));
        if (unique.length === 0) {
          Swal.showValidationMessage('Please select at least one recipient.');
          return null;
        }

        const subject = subjectEl && subjectEl.value ? String(subjectEl.value).trim() : '';
        const body = bodyEl && bodyEl.value ? String(bodyEl.value) : '';

        if (!subject) {
          Swal.showValidationMessage('Subject is required.');
          return null;
        }

        return { toEmails: unique, subject, body };
      }
    }).then(async result => {
      if (!result.isConfirmed) return;

      const payload = result.value as any;
      const toEmails = payload && payload.toEmails ? (payload.toEmails as string[]) : [];
      const subject = payload && payload.subject ? String(payload.subject) : '';
      const body = payload && payload.body ? String(payload.body) : '';
      if (!toEmails || toEmails.length === 0) return;

      const contentEl = document.getElementById('annual-tax-receipt-content');
      const html = contentEl ? String(contentEl.innerHTML) : '';

      this.showLoadingMessage = true;

      this.reportService.sendAnnualTaxReceiptEmail({
        pharmacyId: this.pharmacyId,
        facilityId: this.facilityId,
        patientId: this.patientId,
        year,
        toEmails,
        subject,
        body,
        fileBaseName,
        html,
        performedByUserId: this.sharedService.getUserId()
      })
        .pipe(timeout(6000000))
        .subscribe(data => {
          this.showLoadingMessage = false;

          if (data && data.success === false) {
            this.loadErrorMessage("Oops! Something went wrong, please try again in a few minutes.");
            return;
          }

          Swal.fire({
            icon: 'success',
            title: 'Email queued',
            html: `
              <p>The annual tax receipt email has been queued for sending.</p>
              <p><strong>Recipients:</strong> ${toEmails.join(', ')}</p>
            `,
            confirmButtonText: 'OK'
          });
        }, error => {
          this.showLoadingMessage = false;

          if (error && error.status === 404) {
            Swal.fire({
              icon: 'info',
              title: 'Not implemented yet',
              text: 'Email sending API is not available yet. Please implement it on the server and try again.',
              confirmButtonText: 'OK'
            });
            return;
          }

          this.loadErrorMessage("Oops! Something went wrong, please try again in a few minutes.");
        });
    });
  }

  @ViewChild('RemoveConfirmModalClosebutton', { static: false }) closebutton;

  get canUpdateProfile(): boolean {
    return this.profileData.sageName != null && this.profileData.sageName.trim().length > 0;
  }

  /** Profile row exists in DB (can clear Sage fields and balance forward) */
  get canDeleteSageProfile(): boolean {
    const c = this.sageBalanceForward && this.sageBalanceForward.customer;
    const id = c ? c.id : null;
    return id != null && id > 0;
  }

  searchSageCustomer() {
    if (!this.sageSearchTerm || !this.sageSearchTerm.trim()) {
      return;
    }
    this.sageSearching = true;
    this.sageSearchResults = [];
    const request: ISageCustomerSearchRequestDto = {
      name: this.sageSearchTerm.trim(),
      accountNumber: '',
      page: 1,
      limit: 10
    };
    this.invoiceService.searchSageCustomer(request).subscribe(
      results => {
        this.sageSearchResults = results || [];
        this.sageSearching = false;
      },
      () => {
        this.sageSearching = false;
        this.setDefaultErrorHandler(null);
      }
    );
  }

  selectSageCustomer(customer: ISageCustomerDto) {
    this.profileData = {
      sageName: customer.name || '',
      sageCustomerId: customer.id != null ? customer.id : null,
      contact: customer.contact || '',
      email: customer.email || '',
      accountNumber: customer.accountNumber || '',
      paymentType: customer.paymentType || '',
      street1: customer.street1 || '',
      street2: customer.street2 || '',
      city: customer.city || '',
      province: customer.province || '',
      postalCode: customer.postalCode || '',
      country: customer.country || 'Canada',
      // Keep email send permission as stored in DB; do not change on search
      canSendEmail: this.sageBalanceForward.customer
        ? this.sageBalanceForward.customer.canSendEmail
        : false
    };
    this.sageSearchResults = [];
    this.sageSearchTerm = '';
  }

  clearSageSearch() {
    this.sageSearchTerm = '';
    this.sageSearchResults = [];
  }

  // Modal editing methods
  openEditModal() {
    this.clearSageSearch();
    const customer = this.sageBalanceForward.customer;
    this.profileData = {
      sageName: customer.sageName || '',
      sageCustomerId: customer.sageCustomerId != null ? customer.sageCustomerId : null,
      contact: customer.contact || '',
      email: customer.email || '',
      accountNumber: customer.accountNumber || '',
      paymentType: customer.paymentType || '',
      street1: customer.street1 || '',
      street2: customer.street2 || '',
      city: customer.city || '',
      province: customer.province || '',
      postalCode: customer.postalCode || '',
      country: customer.country || 'Canada',
      // if DB has value, use it; otherwise default to true
      canSendEmail: typeof customer.canSendEmail === 'boolean' ? customer.canSendEmail : true
    };
  }

  onUpdateProfile() {
    if (!this.profileData.sageName || !this.profileData.sageName.trim()) {
      this.loadErrorMessage('Sage Name cannot be empty.');
      return;
    }

    // Validate email and postal code
    this.validateEmail();
    this.validatePostalCode();

    if (this.emailInvalid || this.postalCodeInvalid) {
      this.loadErrorMessage('Please fix validation errors before saving.');
      return;
    }

    // Get customer name safely
    const customerName = this.sageBalanceForward && this.sageBalanceForward.customer && this.sageBalanceForward.customer.name 
      ? this.sageBalanceForward.customer.name 
      : 'this customer';

    // Show confirmation dialog
    Swal.fire({
      title: 'Update Sage Profile?',
      html: `
        <p>You are about to update the Sage profile information for:</p>
        <p class="fw-bold">${customerName}</p>
        <p class="mt-3">This will update all contact and address information in the system.</p>
        <p class="text-muted small">Please review the information carefully before confirming.</p>
      `,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#198754',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Yes, Update Profile',
      cancelButtonText: 'Cancel'
    }).then((result) => {
      if (result.isConfirmed) {
        this.saveProfile();
      }
    });
  }

  onDeleteSageProfile() {
    if (!this.canDeleteSageProfile) {
      return;
    }

    const customerName = this.sageBalanceForward && this.sageBalanceForward.customer && this.sageBalanceForward.customer.name
      ? this.sageBalanceForward.customer.name
      : 'this patient';

    Swal.fire({
      title: 'Delete Sage profile data?',
      html: `
        <p>This will permanently clear Sage name, customer link, contact, address, email, account, and payment type for:</p>
        <p class="fw-bold">${customerName}</p>
        <p class="text-danger mt-3 mb-0"><strong>All stored balance forward transactions for this patient will be removed.</strong></p>
        <p class="text-muted small mt-2 mb-0">This cannot be undone.</p>
      `,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Yes, delete',
      cancelButtonText: 'Cancel',
      focusCancel: true
    }).then((result) => {
      if (!result.isConfirmed) {
        return;
      }

      this.showLoadingMessage = true;
      this.invoiceService.clearSageProfile(
        this.sageBalanceForward.customer.id,
        this.pharmacyId,
        this.facilityId,
        this.patientId
      ).subscribe(
        res => {
          this.showLoadingMessage = false;
          if (!res.success) {
            this.setDefaultErrorHandler(res.data);
            return;
          }

          this.profileData = {
            sageName: '',
            sageCustomerId: null,
            contact: '',
            email: '',
            accountNumber: '',
            paymentType: '',
            street1: '',
            street2: '',
            city: '',
            province: '',
            postalCode: '',
            country: 'Canada',
            canSendEmail: true
          };

          const c = this.sageBalanceForward.customer;
          c.sageName = '';
          c.sageCustomerId = null;
          c.contact = '';
          c.email = '';
          c.accountNumber = '';
          c.paymentType = '';
          c.street1 = '';
          c.street2 = '';
          c.city = '';
          c.province = '';
          c.postalCode = '';
          c.country = '';
          this.sageBalanceForward.transactionList = [];
          this.sageBalanceForward.balanceForward = 0;
          this.pruneBalanceForwardFilters();

          if (this.closebutton && this.closebutton.nativeElement) {
            this.closebutton.nativeElement.click();
          }

          this.displaySuccessMessage = true;
          this.successMessage = res.data as string || 'Sage profile and balance data were cleared.';
          setTimeout(() => {
            this.displaySuccessMessage = false;
          }, 4000);

          this.loadData(false);
        },
        err => {
          this.showLoadingMessage = false;
          const errPayload = err && err.error && err.error.data !== undefined ? err.error.data : (err && err.data !== undefined ? err.data : null);
          this.setDefaultErrorHandler(errPayload);
        }
      );
    });
  }

  private saveProfile() {
    this.showLoadingMessage = true;

    this.invoiceService.updateSageProfile(
      this.sageBalanceForward.customer.id,
      this.pharmacyId,
      this.facilityId,
      this.patientId,
      this.profileData
    ).subscribe(
      res => {
        if (!res.success) {
          this.setDefaultErrorHandler(res.data);
          this.showLoadingMessage = false;
          return;
        }

        // Update the customer data
        const customer = this.sageBalanceForward.customer;
        customer.sageName = this.profileData.sageName;
        customer.sageCustomerId = this.profileData.sageCustomerId;
        customer.contact = this.profileData.contact;
        customer.email = this.profileData.email;
        customer.accountNumber = this.profileData.accountNumber;
        customer.paymentType = this.profileData.paymentType;
        customer.street1 = this.profileData.street1;
        customer.street2 = this.profileData.street2;
        customer.city = this.profileData.city;
        customer.province = this.profileData.province;
        customer.postalCode = this.profileData.postalCode;
        customer.country = this.profileData.country;
        customer.canSendEmail = this.profileData.canSendEmail;
        
        this.showLoadingMessage = false;
        
        // Close the modal programmatically
        this.closebutton.nativeElement.click();
        
        // Show success message
        this.displaySuccessMessage = true;
        this.successMessage = 'Sage profile updated successfully.';
        setTimeout(() => {
          this.displaySuccessMessage = false;
        }, 3000);
        
        // Refresh balance forward from Sage
        this.onGetProfileFromSage();
      },
      error => {
        this.setDefaultErrorHandler(error.data);
        this.showLoadingMessage = false;
      }
    );
  }

  // Email validation
  validateEmail() {
    if (!this.profileData.email || this.profileData.email.trim() === '') {
      this.emailInvalid = false;
      return;
    }
    const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    this.emailInvalid = !emailRegex.test(this.profileData.email);
  }

  // Canadian postal code validation (A1A1A1 format - 6 characters, no space)
  validatePostalCode() {
    if (!this.profileData.postalCode || this.profileData.postalCode.trim() === '') {
      this.postalCodeInvalid = false;
      return;
    }
    
    // Remove any spaces and convert to uppercase
    const cleanedCode = this.profileData.postalCode.replace(/\s/g, '').toUpperCase();
    this.profileData.postalCode = cleanedCode;
    
    // Validate: Letter-Digit-Letter-Digit-Letter-Digit (6 characters)
    const postalCodeRegex = /^[A-Z]\d[A-Z]\d[A-Z]\d$/;
    this.postalCodeInvalid = !postalCodeRegex.test(cleanedCode);
  }

  get enablePrint(): boolean {
    if (this.invoiceList.length === 0) return false;

    let invoiceIds = this.invoiceList.filter(item =>
      item.invoiceReviewStatusId === 3
      && item.invoiceSageStatusId >= 3
      && item.total > 0).map(item => item.id);

    return Array.isArray(invoiceIds) && invoiceIds.length > 0;
  }

  constructor(private sharedService: SharedService,
    private invoiceService: InvoiceService,
    private reportService: ReportService,
    private settingsService: SettingsService,
    private router: Router,
    private route: ActivatedRoute,
    private datepipe: DatePipe) { }
  

  ngOnInit() {
    this.sharedService.reloadUserPrivileges();

    this.sageSearchSubscription = this.sageSearchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe(term => {
      this.sageSearchTerm = term;
      if (!term) {
        this.sageSearchResults = [];
        return;
      }
      this.searchSageCustomer();
    });

    this.route.params.subscribe(params => {
      this.pharmacyId = +params['pharmacyId'];
      this.facilityId = +params['facilityId'];
      this.patientId = +params['patientId'];

      if (isNaN(this.pharmacyId)        
        || isNaN(this.facilityId)
        || isNaN(this.patientId))
      {
        this.router.navigate(['invoice/profile']);
        return;
      }

      this.annualTaxReceiptActionLogs = [];
      this.annualTaxReceiptActionLogError = '';
      this.loadAnnualTaxReceiptActionLogs();
    });

    this.loadData(false);
    this.updateAnnualScrollButtonVisibility();
  }

  @HostListener('window:scroll')
  onWindowScroll() {
    this.updateAnnualScrollButtonVisibility();
  }

  onTabChanged() {
    // Bootstrap tabs update DOM classes after click; defer visibility check.
    setTimeout(() => this.updateAnnualScrollButtonVisibility(), 0);
  }

  scrollAnnualInvoicesToTop() {
    const target = document.getElementById('annual-invoices-top');
    if (target && typeof target.scrollIntoView === 'function') {
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }

    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  scrollAnnualInvoicesToBottom() {
    const doc = document.documentElement;
    const scrollTopMax = doc.scrollHeight - doc.clientHeight;
    window.scrollTo({ top: scrollTopMax, behavior: 'smooth' });
  }

  toggleAnnualTaxReceiptActionLog() {
    this.showAnnualTaxReceiptActionLogPanel = !this.showAnnualTaxReceiptActionLogPanel;
    if (this.showAnnualTaxReceiptActionLogPanel) {
      this.loadAnnualTaxReceiptActionLogs();
    } else {
      this.resetAnnualTaxReceiptActionLogPanel(false);
    }
  }

  /** Close/clear log UI; pass keepClosed false when panel is already hidden after route change. */
  private resetAnnualTaxReceiptActionLogPanel(closePanel: boolean = true) {
    if (closePanel) {
      this.showAnnualTaxReceiptActionLogPanel = false;
    }
    this.annualTaxReceiptActionLogs = [];
    this.annualTaxReceiptActionLogError = '';
    this.annualTaxReceiptActionLogLoading = false;
  }

  loadAnnualTaxReceiptActionLogs() {
    if (this.pharmacyId == null || isNaN(this.pharmacyId)
      || this.facilityId == null || isNaN(this.facilityId)
      || this.patientId == null || isNaN(this.patientId)) {
      return;
    }
    this.annualTaxReceiptActionLogLoading = true;
    this.annualTaxReceiptActionLogError = '';
    this.invoiceService.searchUserActionLogs({
      pharmacyId: this.pharmacyId,
      facilityId: this.facilityId,
      patientId: this.patientId,
      actionCodes: [
        'INVOICE_ANNUAL_TAX_RECEIPT_PDF_DOWNLOAD',
        'INVOICE_ANNUAL_TAX_RECEIPT_EMAIL_SEND'
      ],
      maxRows: 200
    }).subscribe(
      (rows) => {
        this.annualTaxReceiptActionLogs = rows && Array.isArray(rows) ? rows : [];
        this.annualTaxReceiptActionLogLoading = false;
      },
      () => {
        this.annualTaxReceiptActionLogLoading = false;
        this.annualTaxReceiptActionLogError = 'Could not load activity log.';
        this.annualTaxReceiptActionLogs = [];
      }
    );
  }

  closeAnnualTaxReceiptActionLogPanel() {
    this.resetAnnualTaxReceiptActionLogPanel(true);
  }

  private updateAnnualScrollButtonVisibility() {
    const y = window.pageYOffset || document.documentElement.scrollTop || 0;
    const winH = window.innerHeight || document.documentElement.clientHeight;
    const docHeight = document.documentElement.scrollHeight;
    const isNearBottom = (y + winH) >= (docHeight - 400);

    const annualPane = document.getElementById('year-tax');
    const isAnnualActive = !!annualPane && annualPane.classList.contains('show') && annualPane.classList.contains('active');

    this.showAnnualScrollTopButton = isAnnualActive && y > 600;
    this.showAnnualScrollBottomButton = isAnnualActive && !isNearBottom;
  }

  onTaxYearChange() {
    this.loadAnnualInvoicePaymentItemsByYear();
  }

  loadAnnualInvoicePaymentItemsByYear() {
    if (this.selectedTaxYear == null) {
      this.annualInvoiceItems = [];
      this.annualInvoiceTotalPays = 0;
      this.annualInvoiceTotalHst = 0;
      this.annualInvoiceTotalInvoice = 0;
      this.annualInvoiceMonthlySummary = [];
      return;
    }

    if (this.pharmacyId == null || isNaN(this.pharmacyId)
      || this.facilityId == null || isNaN(this.facilityId)
      || this.patientId == null || isNaN(this.patientId)) {
      return;
    }

    this.annualInvoiceLoading = true;

    this.invoiceService.getInvoicePaymentItemsByYear(this.pharmacyId, this.facilityId, this.patientId, this.selectedTaxYear).subscribe(
      res => {
        if (!res || !res.success) {
          this.annualInvoiceItems = [];
          this.annualInvoiceTotalPays = 0;
          this.annualInvoiceTotalHst = 0;
          this.annualInvoiceTotalInvoice = 0;
          this.annualInvoiceDateBreak = [];
          this.annualInvoiceDateEnd = [];
          this.annualInvoiceMonthlySummary = [];
          this.annualInvoiceLoading = false;
          this.setDefaultErrorHandler(res ? res.data : null);
          return;
        }

        const allItems = res.data ? (res.data as any[]) : [];
        
        // Filter out rows where fillDate is null for the display list only
        this.annualInvoiceItems = allItems.filter(item => item && item.fillDate != null);

        // Add a visual separator when fillDate group changes
        this.annualInvoiceDateBreak = [];
        this.annualInvoiceDateEnd = [];
        let lastFillDateKey = '';
        for (let i = 0; i < this.annualInvoiceItems.length; i++) {
          const item = this.annualInvoiceItems[i];
          const key = this.datepipe.transform(item && item.fillDate ? item.fillDate : null, 'yyyy-MM-dd') || '';
          this.annualInvoiceDateBreak[i] = i > 0 && key !== lastFillDateKey;
          lastFillDateKey = key;
        }

        // Put separator line at the *end* of the previous date group (bottom border)
        for (let i = 0; i < this.annualInvoiceItems.length; i++) {
          const item = this.annualInvoiceItems[i];
          const key = this.datepipe.transform(item && item.fillDate ? item.fillDate : null, 'yyyy-MM-dd') || '';
          const next = (i + 1) < this.annualInvoiceItems.length ? this.annualInvoiceItems[i + 1] : null;
          const nextKey = this.datepipe.transform(next && next.fillDate ? next.fillDate : null, 'yyyy-MM-dd') || '';
          this.annualInvoiceDateEnd[i] = i < (this.annualInvoiceItems.length - 1) && key !== nextKey;
        }

        // Create distinct list of TransactionMonth + InvoiceNumber + Amount (use all items for monthly summary)
        const distinctInvoices = new Map<string, { transactionMonth: string, amount: number }>();
        
        for (let i = 0; i < allItems.length; i++) {
          const item = allItems[i];
          const transactionMonth = item && item.transactionMonth ? item.transactionMonth : '';
          const invoiceNumber = item && item.invoiceNumber ? item.invoiceNumber : '';
          const amount = item && item.amount != null ? Number(item.amount) : 0;
          
          if (transactionMonth && invoiceNumber) {
            const key = `${transactionMonth}|${invoiceNumber}`;
            if (!distinctInvoices.has(key)) {
              distinctInvoices.set(key, { transactionMonth, amount });
            }
          }
        }

        // Group by TransactionMonth and sum amounts
        let monthlyMap: any = {};
        distinctInvoices.forEach((value) => {
          const monthKey = value.transactionMonth;
          if (monthKey) {
            if (!monthlyMap[monthKey]) {
              monthlyMap[monthKey] = { month: monthKey, invoiceTotal: 0 };
            }
            monthlyMap[monthKey].invoiceTotal += value.amount;
          }
        });

        // Calculate Sub Total and HST from Invoice Total
        const monthKeys = Object.keys(monthlyMap).sort();
        const monthlySummary: any[] = [];
        let totalSubTotal = 0;
        let totalHst = 0;
        let totalInvoice = 0;
        
        for (let i = 0; i < monthKeys.length; i++) {
          const k = monthKeys[i];
          const m = monthlyMap[k];
          const invoiceTotal = Math.round((Number(m.invoiceTotal) || 0) * 100) / 100;
          const subTotal = Math.round((invoiceTotal / 1.13) * 100) / 100;
          const hst = Math.round((invoiceTotal - subTotal) * 100) / 100;
          
          monthlySummary.push({
            month: m.month,
            pays: subTotal,
            hst,
            invoiceTotal
          });
          totalSubTotal += subTotal;
          totalHst += hst;
          totalInvoice += invoiceTotal;
        }
        
        this.annualInvoiceMonthlySummary = monthlySummary;
        this.annualInvoiceTotalPays = Math.round(totalSubTotal * 100) / 100;
        this.annualInvoiceTotalHst = Math.round(totalHst * 100) / 100;
        this.annualInvoiceTotalInvoice = Math.round(totalInvoice * 100) / 100;

        this.annualInvoiceLoading = false;
      },
      error => {
        this.annualInvoiceItems = [];
        this.annualInvoiceTotalPays = 0;
        this.annualInvoiceTotalHst = 0;
        this.annualInvoiceTotalInvoice = 0;
        this.annualInvoiceDateBreak = [];
        this.annualInvoiceDateEnd = [];
        this.annualInvoiceMonthlySummary = [];
        this.annualInvoiceLoading = false;
        this.setDefaultErrorHandler(error && error.data ? error.data : null);
      }
    );
  }

  ngOnDestroy() {
    if (this.sageSearchSubscription) {
      this.sageSearchSubscription.unsubscribe();
    }
    this.sageSearchSubject.complete();
  }

  onSageSearchInput() {
    this.sageSearchSubject.next(this.sageSearchTerm);
  }

  getCustomerAddress(cust: ISageCustomerDto): string {
    if (!cust) return '';
    const parts = [cust.street1, cust.street2, cust.city, cust.province, cust.postalCode].filter(p => p && p.trim());
    return parts.join(', ') || '';
  }

  loadPatientInvoiceList() {
    this.invoiceService.getPatientInvoiceList(this.pharmacyId, this.facilityId, this.patientId).subscribe(
      res => {
        if (!res.success) {
          this.setDefaultErrorHandler(res.data);
        }

        this.invoiceList = res.data as IInvoiceNewVo[];
      },
      error => {
        this.setDefaultErrorHandler(error.data);
      });
  }

  loadData(displaySuccessMessage: boolean) {
    this.showLoadingMessage = true;

    const loadBalanceForward$: Observable<IResultObjVo<ISageBalanceForwardDto>> = this.invoiceService.getPatientBalanceForward(this.pharmacyId, this.facilityId, this.patientId).pipe(
      catchError(err => {
        this.loadErrorMessage("Oops! Something went wrong, please try again in a few minutes.");
        return of(null);
      })
    );

    const loadPatientInvoiceList$: Observable<IResultObjVo<IInvoiceNewVo[]>> = this.invoiceService.getPatientInvoiceList(this.pharmacyId, this.facilityId, this.patientId).pipe(
      catchError(err => {
        this.loadErrorMessage("Oops! Something went wrong, please try again in a few minutes.");
        return of(null);
      })
    );

    forkJoin([loadBalanceForward$, loadPatientInvoiceList$]).subscribe(([loadBalanceForwardResult, loadPatientInvoiceListResult]) => {
      if (loadBalanceForwardResult) {
        this.sageBalanceForward = loadBalanceForwardResult.data as ISageBalanceForwardDto;
        if (this.sageBalanceForward.transactionList && this.sageBalanceForward.transactionList.length > 0) {
          this.currentSortField = 'transactionDate';
          this.sortDirection = 'asc';
          this.sageBalanceForward.transactionList.sort((a, b) =>
            new Date(a.transactionDate).getTime() - new Date(b.transactionDate).getTime());
        }
        this.pruneBalanceForwardFilters();
      }
      if (this.balanceForwardAvailableYears.length > 0 && this.selectedTaxYear == null) {
        this.selectedTaxYear = this.balanceForwardAvailableYears[0];
        this.loadAnnualInvoicePaymentItemsByYear();
      }

      if (loadPatientInvoiceListResult) {
        this.invoiceList = loadPatientInvoiceListResult.data as IInvoiceNewVo[];
      }

      this.showLoadingMessage = false;

      if (displaySuccessMessage) {
        this.profileFromSageSuccessMessage();
      }
    });
  }

  openInvoice(invoiceId) {
    this.router.navigate([]).then(result => { window.open('report/invoice/' + invoiceId + '/' + 'Invoice', '_blank'); });
  }

  createSalesInvoice(invoice) {
    if (this.showLoadingMessage) return;

    this.invoiceService.getInvoiceSageProfile(this.pharmacyId, this.facilityId, this.patientId).subscribe(
      res => {
        const profile = res && res.data;
        if (profile == null || profile.sageCustomerId == null || profile.sageCustomerId === undefined) {
          Swal.fire({
            icon: 'warning',
            title: 'Update Sage Profile Required',
            text: 'Please update Sage Profile.',
          });
          return;
        }
        this.showCreateSalesInvoiceConfirm(invoice);
      },
      () => {
        Swal.fire({
          icon: 'warning',
          title: 'Update Sage Profile Required',
          text: 'Please update Sage Profile.',
        });
      }
    );
  }

  private showCreateSalesInvoiceConfirm(invoice) {
    Swal.fire({
      icon: 'question',
      title: 'Create New Invoice',
      html: `
        <div class="text-start">
          <p class="mb-3">Do you want to create a new invoice with the following details?</p>
          <div class="mb-3">
            <strong>Resident Name:</strong> ${invoice.billedTo}<br>
            <strong>Invoice ID:</strong> ${invoice.id}<br>
            <strong>Invoice Number:</strong> ${invoice.invoiceNumber}<br>
            <strong>Invoice Date:</strong> ${this.datepipe.transform(invoice.startDate, 'yyyy-MM-dd')}
          </div>
          <table class="table table-sm table-borderless mb-0">
            <tbody>
              <tr>
                <td>Taxable:</td>
                <td class="text-end">$${invoice.subTotalTaxableItems.toFixed(2)}</td>
              </tr>
              <tr>
                <td>Non-Taxable:</td>
                <td class="text-end">$${invoice.subTotalNonTaxableItems.toFixed(2)}</td>
              </tr>
              <tr>
                <td>HST:</td>
                <td class="text-end">$${invoice.hst.toFixed(2)}</td>
              </tr>
              <tr class="border-top">
                <td><strong>TOTAL:</strong></td>
                <td class="text-end"><strong>$${invoice.total.toFixed(2)}</strong></td>
              </tr>
            </tbody>
          </table>
        </div>
      `,
      showCancelButton: true,
      cancelButtonText: 'Cancel',
      confirmButtonText: 'Yes, create new invoice',
    }).then(result => {
      if(!result.isConfirmed) {
        return;
      }

      if (this.showLoadingMessage) {
        return;
      }

      this.showLoadingMessage = true;

      Swal.fire({
        title: 'Creating invoice...',
        text: 'This can take 10–20 seconds. Please wait.',
        allowOutsideClick: false,
        allowEscapeKey: false,
        onOpen: () => {
          Swal.showLoading();
        }
      });

      this.invoiceService.createSalesInvoice(this.pharmacyId, this.facilityId, this.patientId, invoice.id)
        // This endpoint can take 8–15+ seconds; keep a generous timeout
        .pipe(
          timeout(6000000),
          finalize(() => {
            // Ensure we always unblock the UI
            Swal.close();
            this.showLoadingMessage = false;
          })
        )
        .subscribe(
        res => {
          if (!res.success) {
            this.setDefaultErrorHandler(res.data);
            return;
          }
          this.profileFromSageSuccessMessage();
          this.loadPatientInvoiceList();
        },
        error => {
          this.setDefaultErrorHandler(error.data);
        }
      );
    });
  } 

  openInvoiceBatch() {
    if (!this.enablePrint) return;

    const selectedInvoiceList = this.invoiceList.filter(item => item.selected
      && item.invoiceReviewStatusId === 3
      && item.invoiceSageStatusId >= 3
      && item.total > 0);

    let invoiceIds = selectedInvoiceList.map(item => item.id);

    if (invoiceIds.length == 0) {
      Swal.fire({
        icon: 'warning',
        title: 'No invoice selected',
        text: 'Please select the invoice you wish to download.',
        confirmButtonText: 'OK'
      });
      return;
    }

    Swal.fire({
      icon: 'question',
      title: 'Download invoice PDF?',
      text: 'Do you want to download the invoice PDF file?',
      showCancelButton: true,
      cancelButtonText: 'Cancel',
      confirmButtonText: 'Yes, download',
    }).then(result => {
      if(!result.isConfirmed) {
        return;
      }

      this.showLoadingMessage = true;

      this.reportService.getInvoiceBatch(invoiceIds)
        .pipe(timeout(6000000))
        .subscribe(blob => {
          const fileUrl = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = fileUrl;
          const now = new Date();
          const formattedDate = now.getFullYear() +
          String(now.getMonth() + 1).padStart(2, '0') +
          String(now.getDate()).padStart(2, '0') +
          String(now.getHours()).padStart(2, '0') +
          String(now.getMinutes()).padStart(2, '0') +
          String(now.getMilliseconds()).padStart(2, '0');

          a.download = `InvoicesBatch_${formattedDate}.pdf`;
          a.click();
          window.URL.revokeObjectURL(fileUrl);
          this.showLoadingMessage = false;
        },
        error => {
          this.showLoadingMessage = false;
          this.loadErrorMessage("Oops! Something went wrong, please try again in a few minutes.");
        }
      )
    });    
  }

  onSendAllInvoicesByEmail() {
    if (!this.enablePrint) return;

    const selectedInvoiceList = this.invoiceList.filter(item => item.selected
      && item.invoiceReviewStatusId === 3
      && item.invoiceSageStatusId >= 3
      && item.total > 0);

    let invoiceIds = selectedInvoiceList.map(item => item.id);
    let invoiceNumbers = selectedInvoiceList.map(item => item.invoiceNumber);

    if (invoiceIds.length == 0) {
      Swal.fire({
        icon: 'warning',
        title: 'No invoice selected',
        text: 'Please select the invoice you wish to send the invoice email.',
        confirmButtonText: 'OK'
      });
      return;
    }

    Swal.fire({
      icon: 'question',
      title: 'Send invoice Email?',
      text: 'Are you sure you want to send the invoice email?',
      showCancelButton: true,
      cancelButtonText: 'Cancel',
      confirmButtonText: 'Yes, Send Invoice Email',
    }).then(result => {
      if(!result.isConfirmed) {
        return;
      }

      this.showLoadingMessage = true;

      this.invoiceService.sendAllInvoicesByEmail(this.sharedService.pharmacyId, this.sharedService.facilityId, invoiceIds)
        .pipe(timeout(6000000))
        .subscribe(data => {
          if (!data.success) {
            this.showLoadingMessage = false;
            this.loadErrorMessage("Oops! Something went wrong, please try again in a few minutes.");
            return;
          }
          this.showLoadingMessage = false;

          Swal.fire({
            icon: 'success',
            title: 'Invoice email sent',
            html: `
              <p>The invoice email has been sent successfully.</p>
              <p><strong>Invoice Numbers:</strong> ${invoiceNumbers}</p>
            `,
            confirmButtonText: 'OK'
          });
        },
        error => {
          this.showLoadingMessage = false;
          this.loadErrorMessage("Oops! Something went wrong, please try again in a few minutes.");
        }
      )
    })
  }

  selectAll(isSelected) {
    this.invoiceList.forEach(item => { item.selected = isSelected });
  }



  openRemoveConfirmModal() {
    this.closebutton.nativeElement.click();
    this.showLoadingMessage = false;
  }

  view(invoice) {
    const invoiceId = isNaN(invoice.id) ? 0 : invoice.id;
    this.router.navigate([`invoice/review-detail/${invoiceId}/${invoice.pharmacyId}/${invoice.facilityId}/${invoice.patientId}/${this.billingDateFormated(invoice.startDate)}`]);
  }

  checkForInvoiceStatus(invoice): boolean {
    const VALID_INVOICE_REVIEW_STATUS = [3, 4, 5];
    return invoice && invoice.invoiceReviewStatusId === 3 && VALID_INVOICE_REVIEW_STATUS.indexOf(invoice.invoiceSageStatusId) >= 0;
  }

  loadErrorMessage(message: any) {
    this.showErrorMessage = true;
    this.errorMessage = message.toString();
    window.scroll(0, 0);
    setTimeout(() => {
      this.showErrorMessage = false;
    }, 5000);
  }

  clearErrorMessage() {
    this.showErrorMessage = false;
    this.errorMessage = "";
  }

  setDefaultErrorHandler(e) {
    console.error('Error: ', e);
    this.loadErrorMessage("Oops! Something went wrong, please try again in a few minutes.");
  }

  billingDateFormated(billableMonthAndYear): string {
    const billingDate = moment(billableMonthAndYear);
    return billingDate.format('YYYY-MM-DD');
  }

  onSorting(field: string, isProfile: boolean) {

    if (isProfile) return this.sortData(field, isProfile, this.sageBalanceForward.transactionList);

    return this.sortData(field, isProfile, this.invoiceList);
  }


  sortData(field: string, isProfile: boolean, list: any[]) {
    if (this.currentSortField === field) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.currentSortField = field;
      this.sortDirection = 'asc';
    }
    list.sort((a, b) => {
      const valueA = a[field];
      const valueB = b[field];

      // Handle dates
      const isDate = valueA instanceof Date || !isNaN(Date.parse(valueA));
      const aVal = isDate ? new Date(valueA).getTime() : valueA.toString().toLowerCase();
      const bVal = isDate ? new Date(valueB).getTime() : valueB.toString().toLowerCase();

      if (aVal < bVal) return this.sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return this.sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }


  async onGetProfileFromSage() {

    if (!this.sageBalanceForward.customer.sageName) {      
      this.loadErrorMessage("Please update the sage name first.");
      return;
    }

    this.showLoadingMessage = true;
    let result = await this.invoiceService.getProfileFromSage(this.pharmacyId, this.facilityId, this.patientId);
    if (!result.success) {
      this.setDefaultErrorHandler(result.data);
      this.showLoadingMessage = false;
      return;
    }
    
    this.loadData(true);
  }

  profileFromSageSuccessMessage() {
    this.displaySuccessMessage = true;
    this.successMessage = "The profile has been successfully retrieved from Sage.";
    setTimeout(() => {
      this.displaySuccessMessage = false;
    }, 5000);
  }


}
